/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
//UI libraries
import Grid from "@material-ui/core/Grid";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import styled from "styled-components";
//custom libraries
import { Button, HeaderLink } from "../components";
import { useCSRValue } from "../context";
import { getCurrentUser } from "../helpers/user-session";
import { useLoginForm } from "../hooks/use-login-form";
import { ReactComponent as Logo } from "./../icons/logo.svg";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const StyledHeader = styled.header`
  ${({ theme }) => `
    background: ${theme.palette.text.primary};
    border-bottom: 4px solid ${theme.palette.secondary.light};
    padding: ${theme.spacing(1)}px ${theme.spacing(3)}px;

    ${theme.breakpoints.down("xs")} {
      padding: ${theme.spacing(1)}px ${theme.spacing(2)}px;
    }

    .header-logo {
      display: flex;
      align-items: center;

      svg {
        margin-right: ${theme.spacing(2)}px;
      }
    }

    .header-title {
      color: white;
      font-size: 18px;
      font-weight: 700;
      line-height: 1;
      margin: 0;

      ${theme.breakpoints.up("sm")} {
        font-size: 16px;
      }
    }

    .MuiButtonBase-root {
      margin-left: ${theme.spacing(2)}px;
    }

    > .MuiGrid-root:first-child {
      min-height: 40px;
    }

    .mobile-view {
    @media (min-width: 769px){
      display: none;
    }
  }
  .desktop-view {
    @media (max-width: 768px){
      display: none;
    }
  }
  .MuiButtonBase-root{
    @media (max-width: 640px){
     margin-left: 0px;
    }
  }
  `}
`;

export interface HeaderProps {
  shopAs: boolean;
  username: boolean;
}

export const Header: React.FC<HeaderProps> = ({ shopAs, username }) => {
  const { t } = useTranslation();
  const { csr } = useCSRValue();
  const [userName, setUserName] = useState("");

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menu, setMenu] = React.useState<number>(0);

  const { logout } = useLoginForm();

  const handleClickForShopAs = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setMenu(1);
    setOpen(!open);
  };

  const handleClickForUser = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setMenu(0);
    setOpen(!open);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(!open);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const renderSwitch = (menu: number) => {
    switch (menu) {
      case 0:
        return <MenuItem onClick={handleLogout}>{t("Header.Logout")}</MenuItem>;
      default:
        return <MenuItem onClick={handleLogout}>{t("Header.Logout")}</MenuItem>;
    }
  };

  useEffect(() => {
    const updateUserInfo = () => {
      const user = getCurrentUser();
      setUserName(user?.fullName || "");
    };

    if (csr) {
      updateUserInfo();
    }
  }, [csr]);

  return (
    <StyledHeader>
      <Grid container justify="space-between" alignItems="center">
        <Grid
          item
          className="header-logo"
          aria-controls="customized-menu"
          style={{
            maxWidth: "360px",
            maxHeight: "50px",
          }}>
          <Logo />
        </Grid>
        <Grid item className="desktop-view">
          {shopAs && (
            <Button variant="text" onClick={handleClickForShopAs}>
              <HeaderLink
                id="shopAs"
                label={t("Header.ShoppingAs")}
                value={
                  csr?.shopAs?.address?.firstName
                    ? `${csr.shopAs.address.firstName} ${csr.shopAs.address.lastName}`
                    : csr?.shopAs?.address?.lastName
                }
              />
            </Button>
          )}

          {username && (
            <>
              <Button variant="text" onClick={handleClickForUser}>
                <HeaderLink
                  id="username"
                  label={userName || t("Header.Username")}
                  value={csr?.logonId}
                />
                <ArrowDropDownIcon style={{ color: "white" }} />
              </Button>
              <Menu
                id="menu-user"
                anchorEl={anchorEl}
                getContentAnchorEl={null}
                keepMounted
                open={open}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                onClose={handleClose}>
                {renderSwitch(menu)}
              </Menu>
            </>
          )}
        </Grid>
      </Grid>
    </StyledHeader>
  );
};
